import { css, html } from "../helpers/webComponents";

/** CSS affecting the {@link TextComponent} */
const styles: string = css`
    .text-container p:first-child {
        margin-top: 0;
    }

    .text-container p:last-child {
        margin-bottom: 0;
    }

    #blinker {
        animation: blinking 0.75s step-end infinite;
    }

    @keyframes blinking {
        0%, 100% {
            opacity: 1;
        }
        50% {
            opacity: 0;
        }
    }
`;

/**
 * Represents the text that is shown to the player with a typing effect.
 */
export class TextComponent extends HTMLElement {
    /** The time each character takes to be typed out in milliseconds */
    public typingSpeed: number = 20;

    /** Container containing the text */
    private _contentContainer: HTMLElement = document.createElement("div");

    /** The original HTML content */
    private _originalHTML: string = "";
    /** Index of the current character being typed */
    private _index: number = 0;
    /** The output HTML content */
    private _output: string = "";

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        const shadowRoot: ShadowRoot = this.attachShadow({ mode: "open" });

        this._contentContainer.classList.add("text-container");

        // Trim the content since in Canvas Component it has some extra whitespace
        this._originalHTML = this.innerHTML.trim();

        // Clear the content so we can type it out
        this.innerHTML = "";

        shadowRoot.append(html`
            <style>
                ${styles}
            </style>
        `);

        shadowRoot.append(this._contentContainer);

        // Stop the typing effect when the user clicks on the text
        this.parentElement!.addEventListener("click", () => {
            this.stopTypingEffect();
        });

        // Stop the typing effect when the space key is pressed
        window.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.code === "Space") {
                this.stopTypingEffect();
            }
        });

        // Start the typing effect
        this.typingEffect();
    }

    /**
     * Starts the typing effect.
     */
    private typingEffect(): void {
        // We reached the end of the content so stop typing
        if (this._index >= this._originalHTML.length) {
            return;
        }

        // Check if we are at the start of an ignore-typing tag
        if (this._originalHTML.startsWith("<ignore-typing-effect>", this._index)) {
            const startTagLength: number = "<ignore-typing-effect>".length;
            const endTag: string = "</ignore-typing-effect>";
            const endIndex: number = this._originalHTML.indexOf(endTag, this._index);

            // No closing tag found so we just append the rest of the content
            if (endIndex === -1) {
                this._output += this._originalHTML.substring(this._index);
                this._index = this._originalHTML.length;
            }
            else {
                // Get the content inside the tags
                const ignoreContent: string = this._originalHTML.substring(
                    this._index + startTagLength,
                    endIndex
                );

                this._output += ignoreContent;
                this._index = endIndex + endTag.length;
            }
        }
        // Check if we are at the start of a tag.
        else if (this._originalHTML[this._index] === "<") {
            const tagEnd: number = this._originalHTML.indexOf(">", this._index);

            // If the tag is not closed we just append the rest of the content
            if (tagEnd === -1) {
                this._output += this._originalHTML.substring(this._index);
                this._index = this._originalHTML.length;
            }
            else {
                const tag: string = this._originalHTML.substring(this._index, tagEnd + 1);
                this._output += tag;
                this._index = tagEnd + 1;
            }
        }
        // If its just text we can append it
        else {
            this._output += this._originalHTML[this._index];
            this._index++;
        }

        this._contentContainer.innerHTML = this._output;

        setTimeout(() => this.typingEffect(), this.typingSpeed);
    }

    /**
     * Stops the typing effect and shows the full text.
     */
    private stopTypingEffect(): void {
        // If we already reached the end just return
        if (this._index >= this._originalHTML.length) {
            return;
        }

        this._index = this._originalHTML.length;
        this._output = this._originalHTML;
        this._contentContainer.innerHTML = this._output;
    }
}
